import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut,
  User,
  UserCredential
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { AuthService as LocalAuthService } from './auth-service';
import { SignupCredentials, LoginCredentials } from '../types/auth';
import { diagnosticsConfig } from '../config/diagnostics';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  firstName?: string;
  lastName?: string;
  phone?: string;
  tenantId?: string;
}

class AuthService {
  private logDiagnostic(message: string, data?: any): void {
    if (diagnosticsConfig.enabled && diagnosticsConfig.showDetailedLogs) {
      console.log(`[🧪 Auth Diagnostics] ${message}`, data || '');
    }
  }

  async signUp(credentials: SignupCredentials): Promise<AuthUser> {
    try {
      this.logDiagnostic('🚀 Starting unified sign up process', { 
        email: credentials.email,
        role: credentials.role,
        emulatorEndpoint: diagnosticsConfig.authEmulatorEndpoint 
      });
      
      console.log('🚀 Starting unified sign up process for:', credentials.email);
      console.log('📡 Making API call to Firebase Auth Emulator:', diagnosticsConfig.authEmulatorEndpoint);
      
      // Validate required fields
      if (!credentials.email || !credentials.password) {
        throw new Error('Email and password are required');
      }

      if (credentials.password.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }
      
      // Create Firebase user
      const userCredential: UserCredential = await createUserWithEmailAndPassword(
        auth, 
        credentials.email, 
        credentials.password
      );
      
      this.logDiagnostic('✅ Firebase user created successfully', {
        uid: userCredential.user.uid,
        email: userCredential.user.email
      });

      console.log('✅ Firebase user created:', {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        emulatorEndpoint: diagnosticsConfig.authEmulatorEndpoint
      });

      // Create local user profile with extended data
      try {
        await LocalAuthService.signUp({
          ...credentials,
          email: credentials.email // Ensure email is passed
        });
        console.log('✅ Local user profile created successfully');
      } catch (localError) {
        console.warn('⚠️ Local user creation failed, but Firebase user exists:', localError);
        // Continue with Firebase user data even if local storage fails
      }
      
      return {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: userCredential.user.displayName,
        firstName: credentials.firstName,
        lastName: credentials.lastName,
        phone: credentials.phone,
        tenantId: credentials.tenantId
      };
    } catch (error: any) {
      this.logDiagnostic('❌ Sign up failed', { 
        error: error.message, 
        code: error.code,
        emulatorEndpoint: diagnosticsConfig.authEmulatorEndpoint 
      });
      
      console.error('❌ Sign up failed - Emulator response error:', error.message);
      console.error('🔗 Attempted endpoint:', diagnosticsConfig.authEmulatorEndpoint);
      
      // Provide user-friendly error messages
      let friendlyMessage = error.message;
      if (error.code === 'auth/email-already-in-use') {
        friendlyMessage = 'An account with this email already exists. Please try logging in instead.';
      } else if (error.code === 'auth/weak-password') {
        friendlyMessage = 'Password is too weak. Please choose a stronger password.';
      } else if (error.code === 'auth/invalid-email') {
        friendlyMessage = 'Please enter a valid email address.';
      } else if (error.code === 'auth/network-request-failed') {
        friendlyMessage = 'Network error. Please check that the Firebase Emulator is running.';
      }
      
      throw new Error(friendlyMessage);
    }
  }

  async signIn(credentials: LoginCredentials): Promise<AuthUser> {
    try {
      this.logDiagnostic('🔐 Starting unified sign in process', { 
        email: credentials.email,
        emulatorEndpoint: diagnosticsConfig.authEmulatorEndpoint 
      });
      
      console.log('🔐 Starting unified sign in process for:', credentials.email);
      console.log('📡 Making API call to Firebase Auth Emulator (localhost:9099)');
      
      // Authenticate with Firebase
      const userCredential: UserCredential = await signInWithEmailAndPassword(
        auth, 
        credentials.email, 
        credentials.password
      );
      
      this.logDiagnostic('✅ Firebase sign in successful', {
        uid: userCredential.user.uid,
        email: userCredential.user.email
      });

      console.log('✅ Firebase sign in successful:', {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        emulatorEndpoint: 'http://localhost:9099'
      });

      // Try to get additional user data from local storage
      let additionalData = {};
      try {
        const localUser = await LocalAuthService.login(credentials);
        additionalData = {
          firstName: localUser.name?.first,
          lastName: localUser.name?.last,
          phone: localUser.phone,
          tenantId: localUser.tenantId
        };
        console.log('✅ Local user data retrieved successfully');
      } catch (localError) {
        console.warn('⚠️ Could not retrieve local user data:', localError);
      }
      
      return {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: userCredential.user.displayName,
        ...additionalData
      };
    } catch (error: any) {
      this.logDiagnostic('❌ Sign in failed', { error: error.message, code: error.code });
      console.error('❌ Sign in failed - Emulator response error:', error.message);
      console.error('🔗 Attempted endpoint: http://localhost:9099/identitytoolkit.googleapis.com/v1/accounts:signInWithPassword');
      throw new Error(error.message);
    }
  }

  async signOut(): Promise<void> {
    try {
      this.logDiagnostic('👋 Starting unified sign out process');
      
      console.log('👋 Starting unified sign out process');
      console.log('📡 Making API call to Firebase Auth Emulator (localhost:9099)');
      
      // Sign out from Firebase
      await signOut(auth);
      
      // Clear local auth state
      LocalAuthService.logout();
      
      this.logDiagnostic('✅ Sign out successful');
      console.log('✅ Sign out successful - User session ended');
    } catch (error: any) {
      this.logDiagnostic('❌ Sign out failed', { error: error.message });
      console.error('❌ Sign out failed - Emulator response error:', error.message);
      throw new Error(error.message);
    }
  }

  getCurrentUser(): User | null {
    return auth.currentUser;
  }

  onAuthStateChanged(callback: (user: User | null) => void) {
    return auth.onAuthStateChanged(callback);
  }
}

export const authService = new AuthService();
